import { getDaysUntilExpiry } from '../config/evidenceExpiry';

/**
 * calculateReadinessScore
 * Returns 0-100 composite readiness score for CMMC/NIST assessments.
 *
 * Weights:
 *  Controls   40%
 *  Evidence   25%
 *  POA&M      20%
 *  Narratives 10%
 *  Affirmation 5%
 */
export function calculateReadinessScore(controls, evidence, tasks, affirmations, narratives) {
  // ── 1. Controls (40%) ────────────────────────────────────────
  const totalControls = controls.length;
  let controlScore = 0;
  if (totalControls > 0) {
    const yes     = controls.filter(c => c.answer === 'yes').length;
    const partial = controls.filter(c => c.answer === 'partial').length;
    controlScore  = ((yes + partial * 0.5) / totalControls) * 100;
  }

  // ── 2. Evidence (25%) ────────────────────────────────────────
  const nonExpiredEvidence = evidence.filter(ev => {
    if (!ev.expires_at) return true; // no expiry = valid
    const days = getDaysUntilExpiry(ev.expires_at);
    return days !== null && days >= 0;
  });
  // Score based on: do answered "yes" controls have at least one non-expired evidence?
  let evidenceScore = 0;
  if (totalControls > 0) {
    const yesControls = controls.filter(c => c.answer === 'yes').map(c => c.id);
    if (yesControls.length === 0) {
      evidenceScore = 0;
    } else {
      const coveredIds = new Set(nonExpiredEvidence.flatMap(ev => ev.control_ids || []));
      const covered   = yesControls.filter(id => coveredIds.has(id)).length;
      evidenceScore   = (covered / yesControls.length) * 100;
    }
  }

  // ── 3. POA&M / Tasks (20%) ───────────────────────────────────
  let taskScore = 100; // default full if no tasks
  const nonCompliantControls = controls.filter(c => c.answer === 'no' || c.answer === 'partial').length;
  if (nonCompliantControls > 0) {
    const openTaskControlIds = new Set(
      tasks.filter(t => t.status !== 'done').map(t => t.linked_control_id).filter(Boolean)
    );
    const coveredNC = controls
      .filter(c => c.answer === 'no' || c.answer === 'partial')
      .filter(c => openTaskControlIds.has(c.id)).length;
    taskScore = (coveredNC / nonCompliantControls) * 100;
  }

  // ── 4. Narratives (10%) ──────────────────────────────────────
  let narrativeScore = 0;
  if (totalControls > 0) {
    const narrativeMap = {};
    (narratives || []).forEach(n => { narrativeMap[n.control_id] = n.narrative || ''; });
    const documented = controls.filter(c => (narrativeMap[c.id] || '').length >= 50).length;
    narrativeScore   = (documented / totalControls) * 100;
  }

  // ── 5. Affirmation (5%) ──────────────────────────────────────
  let affirmationScore = 0;
  if (affirmations && affirmations.length > 0) {
    const latest = affirmations.reduce((a, b) =>
      new Date(a.affirmed_at) > new Date(b.affirmed_at) ? a : b
    );
    const daysSince = (Date.now() - new Date(latest.affirmed_at)) / (1000 * 60 * 60 * 24);
    affirmationScore = daysSince <= 365 ? 100 : 0;
  }

  const composite =
    controlScore    * 0.40 +
    evidenceScore   * 0.25 +
    taskScore       * 0.20 +
    narrativeScore  * 0.10 +
    affirmationScore * 0.05;

  return {
    score:            Math.round(composite),
    breakdown: {
      controls:    { score: Math.round(controlScore),     weight: 40 },
      evidence:    { score: Math.round(evidenceScore),    weight: 25 },
      tasks:       { score: Math.round(taskScore),        weight: 20 },
      narratives:  { score: Math.round(narrativeScore),   weight: 10 },
      affirmation: { score: Math.round(affirmationScore), weight: 5  },
    },
  };
}

export function getReadinessLabel(score) {
  if (score >= 90) return { label: 'Assessment Ready',   color: '#22c55e' };
  if (score >= 75) return { label: 'Nearly Ready',       color: '#86efac' };
  if (score >= 50) return { label: 'In Progress',        color: '#f59e0b' };
  if (score >= 25) return { label: 'Early Stage',        color: '#f97316' };
  return             { label: 'Getting Started',         color: '#ef4444' };
}
